import React from "react";
import {
  Building2,
  Check,
  Plus,
  Users,
} from "lucide-react";
import LoadingCard from "../common/LoadingCard";


function WorkspaceSection({
  workspaces,
  workspaceId,
  loading,
  onSelect,
  onAdd,
}) {
 return (
  <section className="rounded-[22px] border border-[#d8e7ed] bg-white p-4 shadow-[0_14px_40px_rgba(15,43,66,0.06)] sm:p-5 dark:border-dark-border dark:bg-dark-card">
    {/* Header */}
    <div className="mb-3 flex flex-col gap-2.5 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8194a0] dark:text-dark-text-muted">
          Destination
        </p>

        <h2 className="mt-1 text-[14px] font-bold leading-5 text-[#173044] dark:text-dark-text-primary">
          Select a workspace
        </h2>
      </div>

      <div className="flex items-center justify-between gap-2 sm:justify-end sm:gap-3">
        <span className="text-[9px] font-medium text-[#8194a0] dark:text-dark-text-muted">
          {workspaces.length} available
        </span>

        {onAdd && <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[#b9dff0] bg-[#edf8fd] px-3 text-[9px] font-bold text-[#147db5] transition-all hover:border-[#8ac8ef] hover:bg-[#e3f0fb] dark:border-dark-primary/45 dark:bg-dark-soft-blue dark:text-dark-primary"
        >
          <Plus size={12} />
          Manage in Auto Appy
        </button>}
      </div>
    </div>

    {/* Workspace List */}
    {loading ? (
      <LoadingCard />
    ) : (
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">
        {workspaces.map((workspace) => {
          const selected =
            String(workspace.id) === String(workspaceId);

          return (
            <button
              key={workspace.id}
              type="button"
              onClick={() => onSelect(workspace.id)}
              className={[
                "group relative min-w-0 overflow-hidden rounded-[16px] border p-3 text-left transition-all duration-200 sm:p-4",
                selected
                  ? "border-[#9bcbe2] bg-[#f1f9fd] shadow-[0_8px_24px_rgba(20,125,181,0.08)] dark:border-dark-primary/50 dark:bg-dark-soft-blue"
                  : "border-[#e2ebef] bg-white hover:-translate-y-0.5 hover:border-[#c9dce4] hover:shadow-[0_8px_22px_rgba(15,43,66,0.06)] dark:border-dark-border dark:bg-dark-surface dark:hover:border-dark-primary/40",
              ].join(" ")}
            >
              {/* Selected Decoration */}
              {selected && (
                <div className="absolute right-0 top-0 h-12 w-12 rounded-bl-full bg-[#dff1f8] sm:h-16 sm:w-16 dark:bg-dark-blue-bg" />
              )}

              <div className="relative flex min-w-0 items-center gap-2.5 sm:gap-3">
                {/* Icon */}
                <div
                  className={[
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors sm:h-11 sm:w-11 sm:rounded-xl",
                    selected
                      ? "bg-[#147db5] text-white shadow-[0_5px_14px_rgba(20,125,181,0.18)] dark:bg-dark-primary"
                      : "bg-[#edf3f6] text-[#607787] dark:bg-dark-surface dark:text-dark-text-secondary",
                  ].join(" ")}
                >
                  <Building2
                    size={16}
                    strokeWidth={1.8}
                    className="sm:hidden"
                  />

                  <Building2
                    size={18}
                    strokeWidth={1.8}
                    className="hidden sm:block"
                  />
                </div>

                {/* Workspace Details */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-semibold text-text-primary sm:text-sm dark:text-dark-text-primary">
                    {workspace.name ||
                      workspace.workspace_name ||
                      "Workspace"}
                  </p>

                  <div className="mt-1 flex min-w-0 items-center gap-1.5 sm:gap-2">
                    <span className="flex min-w-0 items-center gap-1 truncate text-[9px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
                      <Users
                        size={9}
                        className="shrink-0 sm:hidden"
                      />

                      <Users
                        size={10}
                        className="hidden shrink-0 sm:block"
                      />

                      {workspace.autoappy_workspace_id ? "Organisation workspace" : "Existing WhatsApp workspace"}
                    </span>

                    {selected && (
                      <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[8px] font-bold text-[#16805c] sm:text-[9px] dark:bg-dark-surface dark:text-[#65cda7]">
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Selected Check */}
                {selected && (
                  <div className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#147db5] text-white sm:h-6 sm:w-6 dark:bg-dark-primary">
                    <Check
                      size={11}
                      strokeWidth={2.2}
                      className="sm:hidden"
                    />

                    <Check
                      size={13}
                      strokeWidth={2.2}
                      className="hidden sm:block"
                    />
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Add Workspace Card */}
        {onAdd && <button
          type="button"
          onClick={onAdd}
          className="group flex min-h-[64px] items-center justify-center rounded-[16px] border border-dashed border-[#c9dce4] bg-[#f8fbfc] p-3 transition-all duration-200 hover:border-[#9bcbe2] hover:bg-[#edf8fd] sm:min-h-[84px] sm:p-4 dark:border-dark-border dark:bg-dark-surface dark:hover:border-dark-primary/40 dark:hover:bg-dark-soft-blue"
        >
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-app-bg text-text-muted transition-colors group-hover:bg-theme-100 group-hover:text-primary sm:h-10 sm:w-10 sm:rounded-xl dark:bg-dark-surface dark:text-dark-text-muted dark:group-hover:bg-dark-soft-blue dark:group-hover:text-dark-primary">
              <Plus
                size={15}
                strokeWidth={1.8}
              />
            </div>

            <div className="min-w-0 text-left">
              <p className="truncate text-[10px] font-semibold text-text-primary sm:text-xs dark:text-dark-text-primary">
                Manage in Auto Appy
              </p>

              <p className="mt-0.5 truncate text-[9px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
                Organisations and team
              </p>
            </div>
          </div>
        </button>}
      </div>
    )}
  </section>
);
}

export default WorkspaceSection;
